import type { FeatureDefinition } from '../definition/FeatureDefinition';
import type { FeaturePrototype } from '../prototype/FeaturePrototype';

/**
 * 特征编辑会话。
 */
export class FeatureEditSession<
    TDefinition extends FeatureDefinition = FeatureDefinition,
    TPrototype extends FeaturePrototype<TDefinition> = FeaturePrototype<TDefinition>
> {
    /** 会话唯一标识。 */
    private readonly sessionId: string;
    /** 正在编辑的特征原型。 */
    private readonly prototype: TPrototype;
    /** 原始目标命令。 */
    private readonly targetCommand: CadCommand;
    /** 进入编辑前的完整命令历史。 */
    private readonly originalCommands: CadCommand[];
    /** 移除目标及真实下游后的预览沙箱命令。 */
    private readonly previewCommands: CadCommand[];
    /** 目标特征及其传递下游 ID。 */
    private readonly affectedFeatureIds: string[];
    /** 草稿定义对象。 */
    private draftDefinition: TDefinition;

    /**
     * @param input 输入参数。
     */
    public constructor(input: {
        sessionId: string;
        prototype: TPrototype;
        targetCommand: CadCommand;
        originalCommands: CadCommand[];
        previewCommands: CadCommand[];
        affectedFeatureIds: string[];
        draftDefinition?: TDefinition;
    }) {
        this.sessionId = input.sessionId;
        this.prototype = input.prototype;
        this.targetCommand = structuredClone(input.targetCommand);
        this.originalCommands = input.originalCommands.map((command) => structuredClone(command));
        this.previewCommands = input.previewCommands.map((command) => structuredClone(command));
        this.affectedFeatureIds = [...input.affectedFeatureIds];
        this.draftDefinition = input.draftDefinition ?? input.prototype.getDefinition();
    }

    /**
     * 获取会话 ID。
     *
     * @returns 会话 ID。
     */
    public getSessionId(): string {
        return this.sessionId;
    }

    /**
     * 获取目标特征原型。
     *
     * @returns 特征原型。
     */
    public getPrototype(): TPrototype {
        return this.prototype;
    }

    /**
     * 获取目标命令。
     *
     * @returns 目标命令快照。
     */
    public getTargetCommand(): CadCommand {
        return structuredClone(this.targetCommand);
    }

    /**
     * 获取进入编辑前的完整命令历史。
     *
     * @returns 完整命令快照。
     */
    public getOriginalCommands(): CadCommand[] {
        return this.originalCommands.map((command) => structuredClone(command));
    }

    /**
     * 获取编辑预览沙箱命令。
     *
     * @returns 已移除目标及真实下游的命令快照。
     */
    public getPreviewCommands(): CadCommand[] {
        return this.previewCommands.map((command) => structuredClone(command));
    }

    /**
     * 获取本次编辑会话影响的特征 ID。
     *
     * @returns 特征 ID 列表。
     */
    public getAffectedFeatureIds(): string[] {
        return [...this.affectedFeatureIds];
    }

    /**
     * 获取当前草稿定义。
     *
     * @returns 草稿定义对象。
     */
    public getDraftDefinition(): TDefinition {
        return this.draftDefinition;
    }

    /**
     * 更新草稿定义。
     *
     * @param nextDefinition 新的草稿定义。
     */
    public updateDraftDefinition(nextDefinition: TDefinition): void {
        this.draftDefinition = nextDefinition;
    }

    /**
     * 重置草稿定义为原特征定义。
     */
    public resetDraftDefinition(): void {
        this.draftDefinition = this.prototype.getDefinition();
    }
}
